import * as Sharing from 'expo-sharing';
import { joinRange } from '../utils/cvDates';
import { DEFAULT_ACCENT, readableOn } from '../theme/templates';
import type { CvTemplate, GeneratedCv, OutputLanguage } from '../types';
import { cvSectionLabel, type CvSectionKey } from '../utils/documentLabels';

export interface CvMeta {
  name?: string;
  title?: string;
  // Coordonnées (viennent du profil plus tard) — affichées si présentes, sinon masquées.
  address?: string;
  email?: string;
  phone?: string;
  linkedin?: string;
  template?: CvTemplate;
  accentColor?: string;
  // Langue du DOCUMENT, pas de l'interface : les intitulés de sections du PDF la suivent. Un CV
  // français exporté depuis une application en anglais reste entièrement français.
  outputLanguage?: OutputLanguage;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function section(label: string, inner: string): string {
  return `<section><h3>${escapeHtml(label)}</h3>${inner}</section>`;
}

function inlineList(items: string[]): string {
  return `<p class="inline">${items.map(escapeHtml).join(' · ')}</p>`;
}

// Entrée datée en deux colonnes : date à gauche, contenu (titre gras + meta + bullets) à droite.
function datedEntry(date: string, title: string, meta: string, bullets: string[]): string {
  return `
    <div class="entry">
      <div class="entry-date">${escapeHtml(date)}</div>
      <div class="entry-body">
        <p class="item-title">${escapeHtml(title)}</p>
        ${meta ? `<p class="item-meta">${escapeHtml(meta)}</p>` : ''}
        ${bullets.length ? `<ul>${bullets.map((b) => `<li>${escapeHtml(b)}</li>`).join('')}</ul>` : ''}
      </div>
    </div>`;
}

/**
 * DENSITÉ : 0 = le rendu par défaut, 1 à 3 = espacements resserrés pour tenir sur UNE page A4.
 *
 * Seuls l'interligne et les espacements bougent. Les polices sont déjà au plancher fixé
 * (corps 9,5 pt et mentions 8,5 pt en Professionnel, moins encore en Coloré) et la marge du
 * Professionnel à 12 mm : on ne descend pas en dessous, et on ne monte jamais au-dessus du défaut.
 */
export const NIVEAUX_DENSITE = 4;
const DENSITE_MONO = [
  { lh: 1.3, entete: 8, h3Haut: 11, h3Bas: 6, entree: 7, ul: 2, li: 1, titre: 6 },
  { lh: 1.24, entete: 6, h3Haut: 8, h3Bas: 4, entree: 5, ul: 1, li: 0.5, titre: 4 },
  { lh: 1.18, entete: 4, h3Haut: 6, h3Bas: 3, entree: 3, ul: 1, li: 0, titre: 3 },
  { lh: 1.12, entete: 3, h3Haut: 4, h3Bas: 2, entree: 2, ul: 0, li: 0, titre: 2 },
];
const DENSITE_ACCENT = [
  { lh: 1.2, h3Haut: 7, h3Bas: 4, entree: 4, ul: 1, h4Haut: 8, titre: 4 },
  { lh: 1.16, h3Haut: 5, h3Bas: 3, entree: 3, ul: 1, h4Haut: 6, titre: 3 },
  { lh: 1.12, h3Haut: 4, h3Bas: 2, entree: 2, ul: 0, h4Haut: 5, titre: 2 },
  { lh: 1.08, h3Haut: 3, h3Bas: 2, entree: 1, ul: 0, h4Haut: 4, titre: 2 },
];
const niveauBorne = (niveau: number) => Math.max(0, Math.min(NIVEAUX_DENSITE - 1, Math.floor(niveau)));

// Pièces communes aux deux templates, calculées une fois.
function cvParts(cv: GeneratedCv, meta: CvMeta) {
  const label = (key: CvSectionKey) => cvSectionLabel(key, meta.outputLanguage ?? 'fr');
  const contactLines = (
    [
      ['Email', meta.email],
      ['Tel', meta.phone],
      ['LinkedIn', meta.linkedin],
      ['Adresse', meta.address],
    ] as const
  )
    .filter(([, value]) => value?.trim())
    .map(([label, value]) => `<p class="contact">${escapeHtml(`${label} : ${value!.trim()}`)}</p>`)
    .join('');

  const headline = (cv.headline?.trim() || meta.title?.trim()) ?? '';
  const name = meta.name?.trim() ?? '';

  const experiences = cv.experiences.length
    ? section(
        label('experiences'),
        cv.experiences
          .map((exp) => datedEntry(joinRange(exp.start_date, exp.end_date), exp.role, exp.company, exp.bullets))
          .join(''),
      )
    : '';
  const projects = cv.projects.length
    ? section(label('projects'), cv.projects.map((p) => datedEntry(p.date, p.name, '', p.bullets)).join(''))
    : '';
  const education = cv.education.length
    ? section(label('education'), cv.education.map((e) => datedEntry(e.date, e.degree, e.school, [])).join(''))
    : '';
  const interests = cv.interests.length ? section(label('interests'), inlineList(cv.interests)) : '';

  return { contactLines, headline, name, experiences, projects, education, interests };
}

// Template Mono (défaut) : noir & blanc, en-tête à gauche + titre centré, deux colonnes.
function buildMonoHtml(cv: GeneratedCv, meta: CvMeta, niveau = 0): string {
  const p = cvParts(cv, meta);
  const d = DENSITE_MONO[niveauBorne(niveau)];
  const label = (key: CvSectionKey) => cvSectionLabel(key, meta.outputLanguage ?? 'fr');
  const header = `
    <header>
      <div class="head-left">
        ${p.name ? `<h1>${escapeHtml(p.name)}</h1>` : ''}
        ${p.contactLines}
      </div>
      ${p.headline ? `<h2 class="headline">${escapeHtml(p.headline)}</h2>` : ''}
    </header>`;
  const skills = cv.skills.length ? section(label('skills'), inlineList(cv.skills)) : '';
  const languages = cv.languages.length ? section(label('languages'), inlineList(cv.languages)) : '';

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8" />
<style>
  /* Noir & blanc uniquement (ATS-friendly). Densité « une page A4 » + cap 3 bullets. */
  @page { size: A4; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #000; font-size: 9.5pt; line-height: ${d.lh}; }
  header { padding-bottom: 4px; margin-bottom: ${d.entete}px; }
  .head-left { text-align: left; }
  h1 { font-size: 8.5pt; color: #000; margin: 0 0 2px; font-weight: 700; }
  .contact { font-size: 8.5pt; color: #000; margin: 0; line-height: 1.25; }
  h2.headline { font-size: 12pt; color: #000; text-align: center; margin: ${d.titre}px 0 0; font-weight: 600; }
  h3 { font-size: 10.5pt; color: #000; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #000; padding-bottom: 2px; margin: ${d.h3Haut}px 0 ${d.h3Bas}px; }
  p { margin: 0; }
  section { margin-bottom: 2px; }
  .entry { display: flex; margin-bottom: ${d.entree}px; }
  .entry-date { width: 22%; padding-right: 8px; font-size: 8.5pt; color: #000; }
  .entry-body { width: 78%; }
  .item-title { font-weight: 700; }
  .item-meta { color: #000; font-size: 9pt; }
  ul { margin: ${d.ul}px 0 0; padding-left: 15px; }
  li { margin-bottom: ${d.li}px; }
  .inline { color: #000; }
</style>
</head>
<body>
${header}
${p.experiences}
${p.projects}
${p.education}
${skills}
${languages}
${p.interests}
</body>
</html>`;
}

// Template Coloré : bande latérale teintée (nom/coordonnées/compétences/langues) + contenu à droite.
// Densifié pour tenir sur une seule page A4 même avec un CV bien rempli.
function buildAccentHtml(cv: GeneratedCv, meta: CvMeta, niveau = 0): string {
  const label = (key: CvSectionKey) => cvSectionLabel(key, meta.outputLanguage ?? 'fr');
  const d = DENSITE_ACCENT[niveauBorne(niveau)];
  const p = cvParts(cv, meta);
  const accent = meta.accentColor || DEFAULT_ACCENT;
  const on = readableOn(accent); // texte lisible SUR la bande colorée
  // Texte sur fond blanc (titres/headline) : l'accent foncé reste lisible, l'accent clair tombe en noir.
  const accentText = on === '#FFFFFF' ? accent : '#111111';
  const sideBlock = (heading: string, items: string[]) =>
    items.length
      ? `<h4>${escapeHtml(heading)}</h4>${items.map((i) => `<p class="side-item">${escapeHtml(i)}</p>`).join('')}`
      : '';

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8" />
<style>
  /* Force le rendu des fonds de couleur à l'impression (sinon la bande latérale disparaît). */
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  @page { size: A4; margin: 0; }
  /* Hauteur relative à la PAGE (pas un mm codé en dur) : la bande descend toute la hauteur sans dépasser. */
  html, body { height: 100%; margin: 0; padding: 0; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #000; font-size: 8.5pt; line-height: ${d.lh}; }
  .sheet { display: flex; min-height: 100%; }
  .sidebar { width: 30%; background: ${accent}; color: ${on}; padding: 8mm 6mm; }
  .sidebar h1 { font-size: 12pt; margin: 0 0 4px; color: ${on}; }
  .sidebar .contact { font-size: 8pt; margin: 0; line-height: 1.28; color: ${on}; }
  .sidebar h4 { font-size: 9pt; text-transform: uppercase; letter-spacing: 0.5px; margin: ${d.h4Haut}px 0 3px; padding-bottom: 2px; border-bottom: 1px solid ${on}; color: ${on}; }
  .sidebar .side-item { font-size: 8pt; line-height: 1.28; margin: 0; color: ${on}; }
  .main { flex: 1; padding: 8mm 7mm; }
  .main h2.headline { font-size: 13pt; color: ${accentText}; margin: 0 0 ${d.titre}px; font-weight: 700; }
  h3 { font-size: 10pt; color: ${accentText}; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid ${accentText}; padding-bottom: 2px; margin: ${d.h3Haut}px 0 ${d.h3Bas}px; }
  p { margin: 0; }
  section { margin-bottom: 1px; }
  .entry { display: flex; margin-bottom: ${d.entree}px; }
  .entry-date { width: 24%; padding-right: 6px; font-size: 8pt; }
  .entry-body { width: 76%; }
  .item-title { font-weight: 700; }
  .item-meta { font-size: 8pt; }
  ul { margin: ${d.ul}px 0 0; padding-left: 13px; }
  li { margin-bottom: 0; line-height: ${d.lh}; }
</style>
</head>
<body>
  <div class="sheet">
    <aside class="sidebar">
      ${p.name ? `<h1>${escapeHtml(p.name)}</h1>` : ''}
      ${p.contactLines}
      ${sideBlock(label('skills'), cv.skills)}
      ${sideBlock(label('languages'), cv.languages)}
    </aside>
    <main class="main">
      ${p.headline ? `<h2 class="headline">${escapeHtml(p.headline)}</h2>` : ''}
      ${p.experiences}
      ${p.projects}
      ${p.education}
      ${p.interests}
    </main>
  </div>
</body>
</html>`;
}

// HTML d'un CV A4 selon le template choisi (texte réel, sélectionnable → ATS-friendly).
export function buildCvHtml(cv: GeneratedCv, meta: CvMeta = {}, niveau = 0): string {
  return meta.template === 'accent' ? buildAccentHtml(cv, meta, niveau) : buildMonoHtml(cv, meta, niveau);
}

// Ouvre la feuille de partage native (uniquement APRÈS acceptation du gate).
export async function shareCvPdf(uri: string): Promise<void> {
  const available = await Sharing.isAvailableAsync();
  if (!available) {
    throw new Error('Le partage n’est pas disponible sur cet appareil.');
  }
  await Sharing.shareAsync(uri, { mimeType: 'application/pdf', UTI: 'com.adobe.pdf' });
}
